/**
 * The company layout: the gates, then the suite's AppShell around the page.
 *   1. Link Accounting is off for the workspace → a whole page saying so.
 *   2. A company the person doesn't hold → reported as lost (told, then home).
 *   3. Read-only or past due → a warning banner (past due stays writable); a trial → its end date.
 * The shell carries no notification bell; its one nav badge counts the review
 * queue's open items assigned to the person.
 */
import { useEffect, type JSX } from "react";
import {
  Navigate,
  Outlet,
  createFileRoute,
  useNavigate,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeftRight,
  Boxes,
  CalendarRange,
  ChartColumn,
  ClipboardCheck,
  Coins,
  FileSpreadsheet,
  FileText,
  House,
  Landmark,
  Layers,
  ListChecks,
  ListTree,
  NotebookPen,
  Receipt,
  Scale,
  Send,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Signpost,
  Truck,
  Users,
} from "lucide-react";
import { performLogout } from "@linkhr/auth";
import { useAuth } from "@linkhr/auth/react";
import { AuthSplash } from "@linkhr/auth/pages";
import {
  Alert,
  AppShell,
  EmptyState,
  ErrorScreen,
  Segmented,
  WorkspaceSwitcher,
  nonEmpty,
  type NavConfig,
} from "@linkhr/design-system";
import { intlDateLocale, useTranslation } from "@linkhr/i18n";
import { useMePermissions, type MePermissionsView } from "../api/useMePermissions";
import { useWorkItems, workItemTotal } from "../api/bills";
import { accountingCompanies, gateFor, holds, ownsCompany, type Gate } from "../shell/gate";
import { companyAccessLost } from "../shell/companyAccess";
import { FullPageNotice } from "../shell/FullPageNotice";
import { CODES, CODE_SETS, OPEN_ITEM_CODES, booksGate, codesOf } from "../ledger/books";
import { useViewMode, type ViewMode } from "../shell/useViewMode";
import { env } from "../env";

export const Route = createFileRoute("/_auth/c/$companyId")({
  component: CompanyLayout,
});

function CompanyLayout(): JSX.Element {
  const { companyId } = Route.useParams();
  const { t } = useTranslation("accounting");
  const permissions = useMePermissions();
  const data = permissions.data;
  const gate = data === undefined ? null : gateFor(data, companyId);
  const lost = gate?.kind === "no_company";
  useEffect(() => {
    if (lost) companyAccessLost(companyId);
  }, [lost, companyId]);

  if (data === undefined || gate === null) {
    return permissions.isError ? (
      <ErrorScreen error={permissions.error} reset={() => void permissions.refetch()} />
    ) : (
      <AuthSplash />
    );
  }
  if (gate.kind === "no_company") return <AuthSplash />;
  if (gate.kind === "product_off") {
    return (
      <FullPageNotice
        heading={t("accounting:app.product_off.title")}
        body={t(`accounting:app.product_off.body.${gate.state}`)}
      />
    );
  }
  return <CompanyShell companyId={companyId} gate={gate} permissions={data} />;
}

function CompanyShell({
  companyId,
  gate,
  permissions,
}: {
  companyId: string;
  gate: Extract<Gate, { company: unknown }>;
  permissions: MePermissionsView;
}): JSX.Element {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { t, i18n } = useTranslation("accounting");
  const locale: "en" | "ar" = i18n.language.startsWith("ar") ? "ar" : "en";
  const base = `/c/${companyId}`;
  const codes = codesOf(gate.company);
  const view = useViewMode(companyId);
  const books = booksGate(
    gate.company.booksReady,
    codes.includes(CODES.settings),
    pathname.slice(base.length).replace(/^\/|\/$/g, ""),
  );

  // A row shows when its page opens for this person: the books' pages once they are set
  // up (anyone with access reads periods, the chart, default accounts and cost centres).
  const ready = gate.company.booksReady;
  const settings = [
    ...(ready
      ? [
          {
            href: `${base}/settings/periods`,
            labelKey: "accounting:ledger.periods.title",
            icon: CalendarRange,
          },
          { href: `${base}/settings/chart`, labelKey: "accounting:nav.chart", icon: ListTree },
          {
            href: `${base}/settings/default-accounts`,
            labelKey: "accounting:app.nav.default_accounts",
            icon: Signpost,
          },
          {
            href: `${base}/settings/cost-centres`,
            labelKey: "accounting:ledger.costCentres.title",
            icon: Layers,
          },
        ]
      : []),
    // The rates list needs the reports code.
    ...(ready && codes.includes(CODES.report)
      ? [{ href: `${base}/settings/fx-rates`, labelKey: "accounting:ledger.fx.title", icon: Coins }]
      : []),
    // Two-person approval and the default reviewer need the settings code.
    ...(ready && codes.includes(CODES.settings)
      ? [
          {
            href: `${base}/settings/approvals`,
            labelKey: "accounting:app.nav.approvals",
            icon: ShieldCheck,
          },
        ]
      : []),
    // Only the account owner manages who has access.
    ...(ownsCompany(gate.company)
      ? [{ href: `${base}/settings/users`, labelKey: "accounting:app.nav.users", icon: Users }]
      : []),
  ];
  const can = (code: string): boolean => ready && holds(gate.company, code);
  // The queue reads with any accounting read code; its badge counts open items assigned to me.
  const reviews = CODE_SETS.R.some(can);
  const assigned = useWorkItems(companyId, { status: "OPEN", assignee: "me", limit: 1 }, reviews);
  const assignedCount = workItemTotal(
    assigned.data?.pages[0]?.counts ?? { byFamily: {}, oldestCreatedAt: null },
  );
  const reports = [
    ...(can(CODES.report)
      ? [
          {
            href: `${base}/reports/trial-balance`,
            labelKey: "accounting:ledger.reports.nav.trial_balance",
            icon: Scale,
          },
          {
            href: `${base}/reports/ledger`,
            labelKey: "accounting:ledger.reports.nav.ledger",
            icon: FileSpreadsheet,
          },
        ]
      : []),
    ...([...OPEN_ITEM_CODES.PAYABLE, ...OPEN_ITEM_CODES.RECEIVABLE].some(can)
      ? [
          {
            href: `${base}/reports/open-items`,
            labelKey: "accounting:ledger.reports.nav.open_items",
            icon: ListChecks,
          },
        ]
      : []),
  ];
  const nav: NavConfig = [
    {
      labelKey: "accounting:nav.group_home",
      icon: House,
      entries: [
        { href: base, labelKey: "accounting:nav.home", icon: House },
        ...(reviews
          ? [
              {
                href: `${base}/review`,
                labelKey: "accounting:nav.review",
                icon: ClipboardCheck,
                badgeKey: "review",
              },
            ]
          : []),
      ],
    },
    // Bills, credits and suppliers read with a purchases or documents read code; payments
    // also with any payment code, so a payment-only role still finds them.
    ...(CODE_SETS.PR.some(can)
      ? [
          {
            labelKey: "accounting:nav.group_purchases",
            icon: ShoppingCart,
            entries: [
              ...(CODE_SETS.P.some(can)
                ? [
                    {
                      href: `${base}/purchases/bills`,
                      labelKey: "accounting:nav.bills",
                      icon: FileText,
                    },
                    {
                      href: `${base}/purchases/credits`,
                      labelKey: "accounting:app.nav.credits",
                      icon: Receipt,
                    },
                  ]
                : []),
              {
                href: `${base}/purchases/payments`,
                labelKey:
                  view.mode === "accountant"
                    ? "accounting:nav.payments_accountant"
                    : "accounting:nav.payments",
                icon: Send,
              },
              ...(CODE_SETS.P.some(can)
                ? [
                    {
                      href: `${base}/purchases/suppliers`,
                      labelKey: "accounting:nav.suppliers",
                      icon: Truck,
                    },
                  ]
                : []),
            ],
          },
        ]
      : []),
    // Accounts read with a bank read code; movements also with drafting, so a drafter finds them.
    ...(CODE_SETS.MOVE.some(can)
      ? [
          {
            labelKey: "accounting:nav.group_banking",
            icon: Landmark,
            entries: [
              ...(CODE_SETS.BANK.some(can)
                ? [{ href: `${base}/banking`, labelKey: "accounting:nav.banking", icon: Landmark }]
                : []),
              {
                href: `${base}/banking/movements`,
                labelKey:
                  view.mode === "accountant"
                    ? "accounting:nav.bank_movements"
                    : "accounting:easy.title",
                icon: ArrowLeftRight,
              },
            ],
          },
        ]
      : []),
    // Drafts, reads included, need the journal permission.
    ...(can(CODES.journal)
      ? [
          {
            labelKey: "accounting:ledger.journal.nav.group",
            icon: NotebookPen,
            entries: [
              {
                href: `${base}/journals`,
                labelKey: "accounting:ledger.journal.nav.manual",
                icon: NotebookPen,
              },
              {
                href: `${base}/journals/stock-count`,
                labelKey: "accounting:ledger.journal.nav.stock_count",
                icon: Boxes,
              },
            ],
          },
        ]
      : []),
    // Trial balance and ledger need report:read; open items any code that reads one of its sides.
    ...(reports.length > 0
      ? [{ labelKey: "accounting:ledger.reports.nav.group", icon: ChartColumn, entries: reports }]
      : []),
    ...(settings.length > 0
      ? [{ labelKey: "accounting:app.nav.settings", icon: Settings, entries: settings }]
      : []),
  ];

  const banner =
    gate.kind === "read_only" || gate.kind === "past_due" ? (
      <Alert
        variant="warning"
        description={
          gate.kind === "past_due"
            ? t("accounting:app.banner.past_due")
            : t("accounting:shell.banner.read_only")
        }
      />
    ) : gate.kind === "trial" && gate.until !== null ? (
      <Alert
        variant="info"
        description={t("accounting:app.banner.trial", {
          date: new Date(gate.until).toLocaleDateString(intlDateLocale(locale), {
            day: "numeric",
            month: "short",
            year: "numeric",
          }),
        })}
      />
    ) : null;

  return (
    <AppShell
      nav={nav}
      aud="accounting"
      appName={t("accounting:shell.app_name")}
      user={{
        displayName: auth.user?.displayName ?? "",
        email: auth.user?.email !== "" ? (auth.user?.email ?? null) : null,
        roles: [],
      }}
      tenantName={
        locale === "ar" ? gate.company.nameAr : (gate.company.nameEn ?? gate.company.nameAr)
      }
      onLogout={() => performLogout({ queryClient, apiBaseUrl: env.VITE_API_BASE_URL })}
      locale={locale}
      onLocaleChange={(next) => void i18n.changeLanguage(next)}
      onNavigate={(href) => void navigate({ to: href })}
      navBasePath={router.basepath}
      // An entry belongs under Journals; a prefix match alone would light Home.
      activeHref={pathname.startsWith(`${base}/entries/`) ? `${base}/journals` : pathname}
      navBadges={{ review: assignedCount }}
      personaSlot={
        <WorkspaceSwitcher
          className="max-w-40 md:max-w-72"
          items={accountingCompanies(permissions).map((c) => ({
            id: c.id,
            nameAr: c.nameAr,
            nameEn: c.nameEn ?? c.nameAr,
            meta: t("accounting:app.company_meta", {
              country: t(`accounting:app.country.${c.country}`),
              currency: c.functionalCurrency,
            }),
          }))}
          currentId={companyId}
          locale={locale}
          // Switching company keeps the page.
          onSelect={(id) => void navigate({ to: `/c/${id}${pathname.slice(base.length)}` })}
          triggerLabel={t("accounting:shell.company_switcher")}
          searchPlaceholder={t("accounting:shell.company_search")}
          emptyLabel={t("accounting:shell.company_none")}
        />
      }
      tenantContent={
        <span className="text-label font-semibold text-default">
          {t("accounting:shell.app_name")}
        </span>
      }
      desktopOnly={false}
      // The view changes only the purchases and banking screens, so only their readers get the switch.
      userMenuExtra={
        [...CODE_SETS.PR, ...CODE_SETS.D, ...CODE_SETS.A, ...CODE_SETS.MOVE].some((code) =>
          codes.includes(code),
        ) ? (
          <div className="flex flex-col gap-xs">
            <span className="text-label text-muted">{t("accounting:app.view_label")}</span>
            <Segmented<ViewMode>
              aria-label={nonEmpty(t("accounting:app.view_label"))}
              value={view.mode}
              onChange={view.setMode}
              options={[
                { value: "business", label: nonEmpty(t("accounting:mode.owner")) },
                { value: "accountant", label: nonEmpty(t("accounting:mode.accountant")) },
              ]}
            />
          </div>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-md">
        {banner}
        {books === "open" ? (
          <Outlet />
        ) : books === "setup" ? (
          <Navigate to="/c/$companyId/setup" params={{ companyId }} replace />
        ) : books === "home" ? (
          <Navigate to="/c/$companyId" params={{ companyId }} replace />
        ) : (
          <EmptyState
            entities={nonEmpty(t("accounting:ledger.books.not_ready_title"))}
            nextAction={nonEmpty(t("accounting:ledger.books.not_ready_body"))}
            heading={t("accounting:ledger.books.not_ready_title")}
            body={t("accounting:ledger.books.not_ready_body")}
          />
        )}
      </div>
    </AppShell>
  );
}
